// Claimable paused-waves list shown beneath the 3 pick-type cards on
// the selector. Any picker at this warehouse can claim a paused wave
// here — clicking POSTs /picking/wave/:id/resume which transitions
// the picker into the active-wave screen.
//
// Paired waves (Alex, 2026-10-16): a half of a paired wave nobody is on
// yet (`awaiting_picker`) renders as a JOIN card ahead of the ordinary
// paused cards — "Join wave · segment 2 of 2 · 83 lines · started by
// Francis 14m ago". Joining goes through the same resume endpoint. A
// paused half WITH progress is an ordinary paused card with its segment
// badge.
//
// 409 from the resume endpoint means another picker beat us to the
// claim, or we already have an active wave — toast + refresh.
import { useTranslation } from "../../i18n";
import { Pause, Users } from "lucide-react";
import { waveTypeLabelKey, type SortStation } from "./selectorShared";

export interface PausedWave {
    wave_id: number;
    pick_type: "light" | "pick_and_sort" | "large";
    // Which station a Pick & Sort wave is bound for; absent on an older BE.
    sort_station?: SortStation | null;
    tier: "high" | "low";
    paused_at: string;
    last_picker_name: string | null;
    paused_by_name: string | null;
    // Items-grained tallies, matching the active wave's
    // {itemsPicked}/{totalItems} counter. The card reads
    // "<items_remaining>/<items_total> left" so a picker resuming a
    // wave they were on sees the same denominator as before.
    items_remaining: number;
    items_total: number;
    // Paired waves (migration 223). All optional: absent on an older BE.
    // awaiting_picker = nobody has been on this half yet → Join card.
    awaiting_picker?: boolean;
    segment_no?: number;
    segment_count?: number;
    lines_total?: number;
    sibling_picker_name?: string | null;
    sibling_started_at?: string | null;
    sibling_status?: string | null;
}

interface Props {
    waves: PausedWave[];
    onClaim: (waveId: number) => void;
    claiming: number | null;
    // True when any wave action (start or resume) is in flight on the
    // parent selector. Locks every paused-wave entry — not just the
    // one being claimed — so the picker can't queue a second claim
    // while the loading overlay is up.
    busy?: boolean;
}

const formatAgo = (raw: string, t: (key: string, vars?: Record<string, string>) => string): string => {
    const ms = Date.now() - new Date(raw).getTime();
    if (ms < 60_000) return t("picking_ago_just_now");
    const minutes = Math.floor(ms / 60_000);
    if (minutes < 60) return t("picking_ago_minutes", { m: String(minutes) });
    const hours = Math.floor(minutes / 60);
    return t("picking_ago_hours", { h: String(hours) });
};

export const isJoinCard = (w: PausedWave): boolean =>
    w.awaiting_picker === true && (w.segment_count ?? 1) > 1;

const PausedWavesList = ({ waves, onClaim, claiming, busy = false }: Props) => {
    const { t } = useTranslation();
    if (waves.length === 0) return null;
    // Join cards first, whatever order the BE sent.
    const ordered = [...waves].sort((a, b) => Number(isJoinCard(b)) - Number(isJoinCard(a)));
    return (
        <div className="picking-paused-section">
            <h2 className="picking-paused-title">{t("picking_paused_section")}</h2>
            <ul className="picking-paused-list">
                {ordered.map(w => {
                    const isClaiming = claiming === w.wave_id;
                    const ago = formatAgo(w.paused_at, t);
                    const by = w.paused_by_name ?? w.last_picker_name;
                    const typeLabel = t(waveTypeLabelKey(w.pick_type, w.sort_station));
                    const segmentBadge = (w.segment_count ?? 1) > 1
                        ? t("picking_segment_badge", { no: String(w.segment_no ?? 1), of: String(w.segment_count) })
                        : null;
                    if (isJoinCard(w)) {
                        const startedAgo = w.sibling_started_at ? formatAgo(w.sibling_started_at, t) : ago;
                        return (
                            <li key={w.wave_id}>
                                <button
                                    type="button"
                                    className="picking-paused-card picking-paused-card--join"
                                    onClick={() => onClaim(w.wave_id)}
                                    disabled={isClaiming || busy}
                                    data-testid={`picking-join-card-${w.wave_id}`}
                                >
                                    <Users size={20} className="picking-paused-icon picking-paused-icon--join" />
                                    <div className="picking-paused-card-body">
                                        <div className="picking-paused-card-title">
                                            {t("picking_join_title", { type: typeLabel })} · {segmentBadge}
                                        </div>
                                        <div className="picking-paused-card-sub">
                                            {t("picking_join_meta", {
                                                lines: String(w.lines_total ?? 0),
                                                items: String(w.items_total),
                                            })}
                                        </div>
                                        <div className="picking-paused-card-meta">
                                            {w.sibling_picker_name
                                                ? t("picking_join_started_by", { name: w.sibling_picker_name, ago: startedAgo })
                                                : t("picking_join_started", { ago: startedAgo })}
                                        </div>
                                    </div>
                                    <span className="picking-paused-claim-cta">
                                        {isClaiming ? t("picking_starting") : t("picking_join_cta")}
                                    </span>
                                </button>
                            </li>
                        );
                    }
                    return (
                        <li key={w.wave_id}>
                            <button
                                type="button"
                                className="picking-paused-card"
                                onClick={() => onClaim(w.wave_id)}
                                disabled={isClaiming || busy}
                            >
                                <Pause size={20} className="picking-paused-icon" />
                                <div className="picking-paused-card-body">
                                    <div className="picking-paused-card-title">
                                        {typeLabel} ·{" "}
                                        {t(w.tier === "high" ? "picking_tier_high" : "picking_tier_low")}
                                        {segmentBadge && <> · {segmentBadge}</>}
                                    </div>
                                    <div className="picking-paused-card-sub">
                                        {t("picking_paused_meta", {
                                            // Show items picked so far / total — matches the
                                            // active-wave counter the picker last saw (e.g.
                                            // "25/30"), rather than a remaining count.
                                            picked: String(Math.max(0, w.items_total - w.items_remaining)),
                                            total: String(w.items_total),
                                        })}
                                    </div>
                                    <div className="picking-paused-card-meta">
                                        {by
                                            ? `${t("picking_paused_by", { name: by })} · ${ago}`
                                            : t("picking_paused_unknown_by", { ago })}
                                    </div>
                                </div>
                                <span className="picking-paused-claim-cta">
                                    {isClaiming ? t("picking_starting") : t("picking_paused_resume")}
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default PausedWavesList;
